import type { IconName } from "./types";

/**
 * Icônes duotones du menu latéral, dessinées pour Safecheck :
 * un contour (currentColor) et un aplat léger de la même teinte, lisibles sur le panneau sombre
 * comme sur la pastille lime de l'élément actif.
 */

type SidebarIconProps = {
  size?: number;
  className?: string;
};

function DuoIcon({ size = 20, className, children }: SidebarIconProps & { children: React.ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Aplat de la couleur courante, sous le contour. */
function Tone({ children }: { children: React.ReactNode }) {
  return (
    <g fill="currentColor" stroke="none" opacity={0.22}>
      {children}
    </g>
  );
}

function DashboardIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <rect x="3" y="3" width="8" height="10" rx="2.5" />
      </Tone>
      <rect x="3" y="3" width="8" height="10" rx="2.5" />
      <rect x="13" y="3" width="8" height="6" rx="2.5" />
      <rect x="13" y="11" width="8" height="10" rx="2.5" />
      <rect x="3" y="15" width="8" height="6" rx="2.5" />
    </DuoIcon>
  );
}

const RECEIPT = "M6 3h12a1 1 0 0 1 1 1v17l-2.5-1.5L14 21l-2-1.5L10 21l-2.5-1.5L5 21V4a1 1 0 0 1 1-1z";

function InvoicesIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d={RECEIPT} />
      </Tone>
      <path d={RECEIPT} />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </DuoIcon>
  );
}

const DOCUMENT = "M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z";

function ReportIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d={DOCUMENT} />
      </Tone>
      <path d={DOCUMENT} />
      <path d="M14 3v5h5" />
      <path d="M9 17v-3M12 17v-6M15 17v-4" />
    </DuoIcon>
  );
}

function RequestsIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <rect x="5" y="4" width="14" height="17" rx="2.5" />
      </Tone>
      {/* Contour ouvert derrière la pince, pour qu'aucun trait ne la traverse. */}
      <path d="M15 4h1.5A2.5 2.5 0 0 1 19 6.5v12a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 5 18.5v-12A2.5 2.5 0 0 1 7.5 4H9" />
      <rect x="9" y="2.5" width="6" height="3.5" rx="1.2" />
      <path d="m9 13.5 2 2 4-4" />
    </DuoIcon>
  );
}

const SHIELD = "M12 3 5 6v5c0 4.5 3 8.3 7 10 4-1.7 7-5.5 7-10V6z";

function CoverageIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d={SHIELD} />
      </Tone>
      <path d={SHIELD} />
      <path d="M12 9v6M9 12h6" />
    </DuoIcon>
  );
}

const PIN = "M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z";

function NetworkIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d={PIN} />
      </Tone>
      <path d={PIN} />
      <path d="M12 7v5M9.5 9.5h5" />
    </DuoIcon>
  );
}

function HospitalIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d="M9 4h6v17H9z" />
      </Tone>
      <path d="M4 21V8a1 1 0 0 1 1-1h4V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3h4a1 1 0 0 1 1 1v13" />
      <path d="M2.5 21h19" />
      <path d="M12 6v4M10 8h4" />
      <path d="M10.5 21v-3.5h3V21" />
      <path d="M7 11h.01M7 15h.01M17 11h.01M17 15h.01" />
    </DuoIcon>
  );
}

function EmployeesIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <circle cx="9" cy="8" r="3.5" />
        <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6z" />
      </Tone>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <path d="M16 4.5a3.5 3.5 0 0 1 0 7" />
      <path d="M17.5 14.2A6 6 0 0 1 21 20" />
    </DuoIcon>
  );
}

function SettingsIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <circle cx="15" cy="6" r="2.2" />
        <circle cx="8" cy="12" r="2.2" />
        <circle cx="13" cy="18" r="2.2" />
      </Tone>
      <path d="M4 6h8.8M17.2 6H20M4 12h1.8M10.2 12H20M4 18h6.8M15.2 18H20" />
      <circle cx="15" cy="6" r="2.2" />
      <circle cx="8" cy="12" r="2.2" />
      <circle cx="13" cy="18" r="2.2" />
    </DuoIcon>
  );
}

const CANOPY = "M12 3a9 9 0 0 1 9 9H3a9 9 0 0 1 9-9z";

function InsurersIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d={CANOPY} />
      </Tone>
      <path d={CANOPY} />
      <path d="M12 12v6.5a2 2 0 0 1-4 0" />
    </DuoIcon>
  );
}

function PackagesIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d="M12 3 3 7.5 12 12l9-4.5z" />
      </Tone>
      <path d="M12 3 3 7.5 12 12l9-4.5z" />
      <path d="m3 12 9 4.5 9-4.5" />
      <path d="m3 16.5 9 4.5 9-4.5" />
    </DuoIcon>
  );
}

function CompaniesIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <rect x="3" y="7" width="18" height="13" rx="2.5" />
      </Tone>
      <rect x="3" y="7" width="18" height="13" rx="2.5" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
      <path d="M3 12.5h18" />
    </DuoIcon>
  );
}

const BOOK = "M5 4.5A1.5 1.5 0 0 1 6.5 3H19v15H6.5A1.5 1.5 0 0 0 5 19.5z";

function ReferentialsIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d={BOOK} />
      </Tone>
      <path d={BOOK} />
      <path d="M5 19.5A1.5 1.5 0 0 0 6.5 21H19v-3" />
      <path d="M12 7v6M9 10h6" />
    </DuoIcon>
  );
}

function ReportingIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      {/* Rails en aplat, barres pleines par-dessus. */}
      <Tone>
        <rect x="7.5" y="5" width="2.6" height="12" rx="1.3" />
        <rect x="11.7" y="5" width="2.6" height="12" rx="1.3" />
        <rect x="15.9" y="5" width="2.6" height="12" rx="1.3" />
      </Tone>
      <path d="M4 4v15a1 1 0 0 0 1 1h15" />
      <g fill="currentColor" stroke="none">
        <rect x="7.5" y="12" width="2.6" height="5" rx="1.3" />
        <rect x="11.7" y="8" width="2.6" height="9" rx="1.3" />
        <rect x="15.9" y="10.5" width="2.6" height="6.5" rx="1.3" />
      </g>
    </DuoIcon>
  );
}

function StatisticsIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d="M15 2.5A8 8 0 0 1 21.5 9H15z" />
      </Tone>
      <path d="M12 3a9 9 0 1 0 9 9h-9z" />
      <path d="M15 2.5A8 8 0 0 1 21.5 9H15z" />
    </DuoIcon>
  );
}

function AccountsIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <rect x="3" y="5" width="18" height="14" rx="2.5" />
      </Tone>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <circle cx="9" cy="11" r="2" />
      <path d="M6 16c.5-1.5 1.7-2.3 3-2.3s2.5.8 3 2.3" />
      <path d="M15 10h3M15 14h3" />
    </DuoIcon>
  );
}

function DeadlinesIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <circle cx="17" cy="17" r="4" />
      </Tone>
      <path d="M20 11V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5" />
      <path d="M8 3v4M16 3v4M4 10h16" />
      <circle cx="17" cy="17" r="4" />
      <path d="M17 15.5V17l1 1" />
    </DuoIcon>
  );
}

function ExchangeIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <circle cx="12" cy="12" r="2" />
      </Tone>
      <path d="M3.5 6.5h16M16.5 3.5l3 3-3 3" />
      <path d="M20.5 17.5h-16M7.5 14.5l-3 3 3 3" />
      <circle cx="12" cy="12" r="2" />
    </DuoIcon>
  );
}

function ControlIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <circle cx="16.5" cy="16.5" r="3" />
      </Tone>
      <path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4" />
      <path d="M13 3v6h6M13 3l6 6v2" />
      <circle cx="16.5" cy="16.5" r="3" />
      <path d="m18.7 18.7 2.3 2.3" />
    </DuoIcon>
  );
}

function SettlementsIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
      </Tone>
      <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 9.5v.01M18 14.5v.01" />
    </DuoIcon>
  );
}

const FOLDER =
  "M3 7.5A2.5 2.5 0 0 1 5.5 5H9l2 2.5h7.5A2.5 2.5 0 0 1 21 10v7.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5z";

function PortfolioIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d={FOLDER} />
      </Tone>
      <path d={FOLDER} />
      <path d="M3 11h18" />
    </DuoIcon>
  );
}

function GridsIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d="M5.5 4h13A2.5 2.5 0 0 1 21 6.5v3H3v-3A2.5 2.5 0 0 1 5.5 4z" />
      </Tone>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M3 9.5h18M3 15h18M9.5 9.5V20" />
    </DuoIcon>
  );
}

const CLOUD = "M7 18a4.5 4.5 0 0 1-.5-8.97A6 6 0 0 1 18 9.5a4 4 0 0 1 0 8.5";

function BackupIcon(props: SidebarIconProps) {
  return (
    <DuoIcon {...props}>
      <Tone>
        <path d={`${CLOUD}z`} />
      </Tone>
      <path d={CLOUD} />
      <path d="M12 20v-7m-3 3 3-3 3 3" />
    </DuoIcon>
  );
}

export const sidebarIcons = {
  dashboard: DashboardIcon,
  invoices: InvoicesIcon,
  report: ReportIcon,
  requests: RequestsIcon,
  coverage: CoverageIcon,
  network: NetworkIcon,
  hospital: HospitalIcon,
  employees: EmployeesIcon,
  settings: SettingsIcon,
  insurers: InsurersIcon,
  packages: PackagesIcon,
  companies: CompaniesIcon,
  referentials: ReferentialsIcon,
  reporting: ReportingIcon,
  statistics: StatisticsIcon,
  accounts: AccountsIcon,
  deadlines: DeadlinesIcon,
  exchange: ExchangeIcon,
  control: ControlIcon,
  settlements: SettlementsIcon,
  portfolio: PortfolioIcon,
  grids: GridsIcon,
  backup: BackupIcon,
} satisfies Record<IconName, React.ComponentType<SidebarIconProps>>;
